// Copyright (c) StrangeBee 2024

import {
    FunctionController,
    FunctionInput,
    FunctionInvokeOutput,
    FunctionOutput,
    FunctionTestInput,
} from 'thehive-sdk';

export interface FunctionEventList {
    onCreate: (data: FunctionInput) => Promise<FunctionOutput | void>;
    onInvoke: (functionId: string) => Promise<FunctionInvokeOutput | void>;
    onTest: (data: FunctionTestInput) => Promise<FunctionInvokeOutput>;
    onDelete: (functionId: string) => Promise<unknown>;
    onEdit: (data: Partial<FunctionInput>, functionId: string) => Promise<unknown>;
}

interface FunctionEventListProps {
    controller: FunctionController;
    onSuccess?: (data?: string) => void;
    onError?: (label?: string) => void;
    locale: (label: string) => string;
}

export const computeEventList = ({
    controller,
    onError,
    onSuccess,
    locale,
}: FunctionEventListProps): FunctionEventList => ({
    onCreate: (data) =>
        controller
            .create(data)
            .then((output) => {
                onSuccess?.(locale('function.manage.success'));
                return output as FunctionOutput | void;
            })
            .catch((error) => {
                onError?.(error);
                return Promise.reject(error);
            }),
    onInvoke: (functionId: string) =>
        controller
            .invoke(functionId)
            .then((output: FunctionInvokeOutput) => {
                onSuccess?.(locale('function.manage.success'));
                return output;
            })
            .catch((error) => {
                onError?.(error);
                return Promise.reject(error);
            }),
    onTest: (data: FunctionTestInput) => controller.test(data).then((output: FunctionInvokeOutput) => output),
    onDelete: (functionId: string) =>
        controller
            .delete(functionId)
            .then((output) => {
                onSuccess?.(locale('function.delete.success'));
                return output;
            })
            .catch((error) => {
                onError?.(error);
                return Promise.reject(error);
            }),
    onEdit: (data: Partial<FunctionInput>, functionId: string) =>
        controller
            .edit<Partial<FunctionInput>, FunctionOutput>(data, functionId)
            .then((output) => {
                onSuccess?.(locale('function.edit.success'));
                return output;
            })
            .catch((error) => {
                onError?.(error);
                return Promise.reject(error);
            }),
});
